import Phaser from 'phaser'
import { TrafficWindow } from '../../../modeles/lines'
import { ImageSvgManager, SvgImages } from '../../../managers/image.manager'
import {
  LiveEvent,
  LiveEvents,
  LiveEventsDto,
  LiveEventsFactory,
  GlobalEventType,
} from '../../../../app/core/models/live-events'
import { UserPreferences } from '../../../../app/core/configurations/user/user.preferences'
import { BehaviorSubject } from 'rxjs'
import { TrackState } from '../../../../app/core/game/services/game-state.service'

export class LiveEventElement extends Phaser.GameObjects.Image {
  private associatedPlatformId: string
  private userPreferences: UserPreferences
  private linkedLiveEvents: LiveEvents | undefined

  constructor({
    scene,
    trafficWindow,
    positionYOfPlatformDisplay,
    userPreferences,
    showStationText,
    newLineMappingConstructionMethod,
  }: {
    scene: Phaser.Scene
    trafficWindow: TrafficWindow
    positionYOfPlatformDisplay: number
    userPreferences: UserPreferences
    showStationText: boolean
    newLineMappingConstructionMethod: boolean
  }) {
    super(scene, 0, 0, '')
    this.setImagePosition({
      positionYOfPlatformDisplay,
      trackNumber: trafficWindow.track,
      showStationText,
      newLineMappingConstructionMethod,
    })
    this.initImageState({ trafficWindow, userPreferences })
  }

  private setImagePosition({ positionYOfPlatformDisplay, trackNumber, showStationText, newLineMappingConstructionMethod }: { positionYOfPlatformDisplay: number; trackNumber: number; showStationText: boolean; newLineMappingConstructionMethod: boolean }): void {
    const X_OFFSET = 22
    const Y_OFFSET = newLineMappingConstructionMethod ? 0 : this.getVerticalFOffset({ trackNumber, showStationText })
    this.setY(positionYOfPlatformDisplay + Y_OFFSET)
    this.setX(X_OFFSET)
  }

  private getVerticalFOffset({ trackNumber, showStationText }: { trackNumber: number; showStationText: boolean }): number {
    const trackOneOffset = 72
    const trackTwoOffset = showStationText ? -52 : -20
    return trackNumber === 2 ? trackTwoOffset : trackOneOffset
  }

  private initImageState({ trafficWindow, userPreferences }: { trafficWindow: TrafficWindow; userPreferences: UserPreferences }): void {
    this.associatedPlatformId = trafficWindow.id
    this.userPreferences = userPreferences
    this.setVisibility(false)
  }

  public updateStateFrom({ platformId, liveEventDto }: { platformId: string; liveEventDto: LiveEventsDto; trackStateSubject: BehaviorSubject<TrackState> }): void {
    this.linkedLiveEvents = new LiveEventsFactory().create({ platformId, liveEventDto })
    if (this.linkedLiveEvents.hasEvents()) {
      this.setImageSrc()
      this.setVisibility(this.isLiveElementVisible())
    } else {
      this.setVisibility(false)
    }
  }

  private setImageSrc(): void {
    const imageReferential: Record<GlobalEventType, SvgImages> = {
      AGENT: SvgImages.LIVE_EVENT_AGENT,
      INCIDENT: SvgImages.LIVE_EVENT_INCIDENT,
      HELPER: SvgImages.LIVE_EVENT_HELPER,
      REQUESTED_AGENT: SvgImages.LIVE_EVENT_REQUESTED_AGENT,
      CONTRACTOR: SvgImages.LIVE_EVENT_CONTRACTOR,
      AGENTS_AND_CONTRACTORS: SvgImages.LIVE_EVENT_AGENTS,
      AGENTS_AND_REQUESTED_AGENTS: SvgImages.LIVE_EVENT_AGENTS,
      HELPERS_AND_AGENTS: SvgImages.LIVE_EVENT_HELPERS,
      INCIDENTS_AND_OTHER: SvgImages.LIVE_EVENT_BOTH,
      SPORT: SvgImages.LIVE_EVENT_PLAN_SPORT,
      MANIFESTATION: SvgImages.LIVE_EVENT_PLAN_MANIFESTATION,
      CULTURAL: SvgImages.LIVE_EVENT_PLAN_CULTURAL,
      OTHER: SvgImages.LIVE_EVENT_PLAN_OTHER,
      WORKS: SvgImages.LIVE_EVENT_PLAN_WORKS
    }
    const icon: string | undefined = ImageSvgManager.getNameOfImage(imageReferential[this.linkedLiveEvents.getGlobalEventType()])

    if (icon) {
      this.setTexture(icon)
    }
  }

  private setVisibility(isVisible: boolean): void {
    this.setVisible(isVisible)
  }

  private isLiveElementVisible(): boolean {
    return !!this.linkedLiveEvents && this.userPreferences.selectedIncidents
  }

  public getLiveEvents(): LiveEvent[] {
    return this.linkedLiveEvents?.getEvents()
  }

  public getPlatformId() {
    return this.associatedPlatformId
  }

  public getEventsCount(): [string, number][] {
    return this.linkedLiveEvents?.getEventsCount()
  }
}
